// Import Types
import type { Sorting, TableDataShape } from 'types';
// Import Helpers
import { generateRequestFilterField } from 'services/Helpers';
// Import Constants
import {
    FilterComparisons,
    StateModesLabels,
    CustomersTypesLabels,
    DEFAULT_SORTING_NAME,
    DEFAULT_SORTING_DIRECTION,
    DEFAULT_SORTING_THEN_NAME,
    DEFAULT_SORTING_THEN_DIRECTION,
    CampaignActivityDefaultPageSize,
} from './common';

const defaultDataTablePageSize = 20;
const defaultDataTablePageNumber = 1;

const generateTableDataShape = (
    defaultFilters,
    sortingName = DEFAULT_SORTING_NAME,
    sortingDirection = DEFAULT_SORTING_DIRECTION,
    sortingThenName = DEFAULT_SORTING_THEN_NAME,
    sortingThenDirection = DEFAULT_SORTING_THEN_DIRECTION,
    shouldIncludeSortingThen = true,
    pageSize = defaultDataTablePageSize,
): TableDataShape => ({
    filters: [...defaultFilters],
    paging: {
        PageSize: pageSize,
        PageNumber: defaultDataTablePageNumber,
    },
    sorting: {
        Name: sortingName,
        Direction: sortingDirection,
    },
    ...(shouldIncludeSortingThen
        ? {
              sortingThen: {
                  Name: sortingThenName,
                  Direction: sortingThenDirection,
              },
          }
        : {}),
});

export const reportInitialState = {
    tableData: generateTableDataShape(
        [generateRequestFilterField('State', FilterComparisons.Equals)],
        'ArchivedDate',
        'asc',
    ),
};

export const campaignInitialState = {
    tableData: generateTableDataShape(
        [generateRequestFilterField('State', FilterComparisons.Equals)],
        'ArchivedDate',
        'asc',
    ),
};

export const campaignExecutionsInitialState = {
    tableData: generateTableDataShape(
        [],
        'StartDate',
        'desc',
        'EndDate',
        'desc',
        true,
        CampaignActivityDefaultPageSize,
    ),
};

export const campaignBudgetHistoryInitialState = {
    tableData: generateTableDataShape([], 'StartDate', 'desc', undefined, undefined, false),
};

export const cjBudgetHistoryInitialState = {
    tableData: generateTableDataShape([], 'StartDate', 'desc', undefined, undefined, false),
};

export const bonusInitialState = {
    tableData: generateTableDataShape([], 'StartDate', 'desc', 'StartDate', 'desc'),
};

export const customerJourneyInitialState = {
    tableData: generateTableDataShape([], 'ArchivedDate', 'asc'),
};

export const templateInitialState = {
    tableData: generateTableDataShape(
        [generateRequestFilterField('State', FilterComparisons.Equals)],
        'ArchivedDate',
        'asc',
    ),
};

export const segmentInitialState = {
    tableData: generateTableDataShape(
        [generateRequestFilterField('State', FilterComparisons.Equals)],
        'ArchivedDate',
        'asc',
    ),
};

export const compositeSegmentInitialState = {
    tableData: generateTableDataShape(
        [
            generateRequestFilterField('State', FilterComparisons.Equals, StateModesLabels.Live),
            generateRequestFilterField('TargetObject', FilterComparisons.Equals, CustomersTypesLabels.Customers),
        ],
        'ArchivedDate',
        'asc',
    ),
};

export const compositeReportInitialState = {
    tableData: generateTableDataShape([generateRequestFilterField('ReportType', FilterComparisons.Equals, ['0'])]),
};

export const campaignPageSegmentInitialState = {
    tableData: generateTableDataShape(
        [generateRequestFilterField('State', FilterComparisons.Equals, StateModesLabels.Live)],
        'ArchivedDate',
        'asc',
    ),
};

export const campaignPageTemplateInitialState = {
    tableData: generateTableDataShape(
        [generateRequestFilterField('State', FilterComparisons.Equals, StateModesLabels.Live)],
        'ArchivedDate',
        'asc',
    ),
};

export const communicationInitialState = {
    tableData: generateTableDataShape([], 'SentDate'),
};

export const campaignPageCommunicationInitialState = {
    tableData: generateTableDataShape([], 'SentDate'),
};

export const customerJourneyPageCommunicationInitialState = {
    tableData: generateTableDataShape([], 'SentDate'),
};

export const adminPartnerSettingsInitialState = {
    tableData: generateTableDataShape([], 'Name', 'asc'),
};

export const adminUsersListInitialState = {
    tableData: generateTableDataShape([], 'UserId', 'asc'),
};

export const adminEmailProvidersInitialState = {
    tableData: generateTableDataShape([], 'EmailProviderId'),
};

export const adminSMSProvidersInitialState = {
    tableData: generateTableDataShape([], 'SmsProviderId'),
};

export const adminPartnersListInitialState = {
    tableData: generateTableDataShape([], 'PartnerId'),
};
export const actionCampaignPageCommunicationInitialState = {
    tableData: generateTableDataShape([], 'SentDate'),
};

export const adminClassifiersListInitialState = {
    tableData: generateTableDataShape([], 'GroupId', 'desc', 'NumId', 'desc'),
};

export const adminMenuItemsListInitialState = {
    tableData: generateTableDataShape([], 'Id'),
};

export const reportHistoryInitialState = {
    tableData: generateTableDataShape([], 'Date', undefined, undefined, undefined, false),
};

export const formulaFilterListInitialState = {
    tableData: generateTableDataShape([], 'CreatedDate', 'desc'),
};

export const achievementListInitialState = {
    tableData: generateTableDataShape([], 'ArchivedDate', 'asc'),
};

export const adminKnowledgeCenterArticlesInitialState = {
    tableData: generateTableDataShape([], 'CreatedDate', 'desc'),
};

export const approvalRequestListInitialState = {
    tableData: generateTableDataShape([], 'CreatedDate', 'desc'),
};

// Here we de not have a default Sorting value, because it is dynamic and with ColumnIds
// We get the sorting value after doing the reportColumns request in the page
export const clientListInitialState = {
    tableData: {
        filters: [],
        paging: {
            PageSize: defaultDataTablePageSize,
            PageNumber: defaultDataTablePageNumber,
        },
        sorting: {} as Sorting,
    },
};
